import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { QUERY_CONFIG, API_ENDPOINTS, buildApiUrl } from '@/constants';
import { api } from '@/utils';

import type { SortingState, ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import type { Call } from '@/types/calls';

interface UseFetchCallsInput {
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    pagination: PaginationState;
    phoneNumber?: string | null;
    campaignId?: string | null;
    qualityFormulaId?: string | null;
    enabled?: boolean;
}

export interface CallAgents {
    id: number;
    name: string;
}

interface UseFetchCallsResponse {
    calls: Call[];
    agents: CallAgents[];
    total: number;
    totalFiltered: number;
    limit: number;
    offset: number;
}

const buildApiParams = ({
    sorting,
    columnFilters,
    pagination,
    phoneNumber,
    campaignId,
    qualityFormulaId,
}: UseFetchCallsInput): Record<string, string> => {
    const offset = pagination.pageIndex * pagination.pageSize;
    const limit = pagination.pageSize;

    const params: Record<string, string> = {
        offset: offset.toString(),
        limit: limit.toString(),
    };

    if (campaignId) {
        params.campaign_id = campaignId;
    }

    if (qualityFormulaId) {
        params.quality_formula_id = qualityFormulaId;
    }

    for (const filter of columnFilters) {
        const { id, value } = filter;
        const filterValue = value as string;

        if (!filterValue || filterValue === 'all') continue;

        switch (id) {
            case 'date_range': {
                params.created_at = filterValue;
                break;
            }
            case 'agent_name': {
                params.agent_id = filterValue;
                break;
            }
            case 'call_duration': {
                params.talk_time = filterValue;
                break;
            }
            case 'direction': {
                params.direction = filterValue;
                break;
            }
            case 'quality_level': {
                params.quality_level = filterValue;
                break;
            }
            case 'reason_name': {
                params.reason_name = filterValue;
                break;
            }
        }
    }

    // Set sorting parameters
    if (sorting.length > 0) {
        const { id, desc } = sorting[0];
        params.sortField = id;
        params.sortOrder = desc ? 'desc' : 'asc';
    }

    if (phoneNumber) {
        params.number = phoneNumber;
    }

    return params;
};

const useFetchCalls = ({
    sorting,
    columnFilters,
    pagination,
    phoneNumber,
    campaignId,
    qualityFormulaId,
    enabled = true,
}: UseFetchCallsInput) => {
    const { data, status, isLoading, isError, error } = useQuery<UseFetchCallsResponse, AxiosError>({
        queryKey: ['calls', sorting, columnFilters, pagination, phoneNumber, campaignId, qualityFormulaId],
        queryFn: async () => {
            const params = buildApiParams({ sorting, columnFilters, pagination, phoneNumber, campaignId, qualityFormulaId });
            const url = buildApiUrl(API_ENDPOINTS.CALLS, params);
            const response = await api.get(url);

            return response.data;
        },
        enabled: enabled && !!campaignId,
        staleTime: 1000 * 60 * 1, // 1 minute
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        gcTime: 0,
        retry: 2,
    });

    return {
        data,
        status,
        isLoading,
        isError,
        error,
    };
};

export default useFetchCalls;
